/**
 * Volumes · field focus desk (map ring · Run · practice covers lab-only).
 *
 * Soft≠Live Page185 product plane:
 *   Live rings always from GET /zones (own Re-read · parent warm-start).
 *   Posture GET /ops/zones/status always-on when registry mounted.
 *   Practice camera covers bind only on 127 — never claim Live player.
 * No Soft invent rings on production; operator copy path-free.
 * Arena HUD v554: match Plant / Cams / Alerts / Send ops-hud language.
 */
import { useEffect, useMemo, useState } from "react";
import { html, go, M, SPRING, SOFT, Ico, Alert } from "./ui.js";
import { path, useApi } from "./api.js";
import { isLocalHost } from "./pathseal.js";
import { LIVE_ZONES_CHANGED } from "./live.js";
import { boardLabelSay } from "./operator-say.js";
import {
  softZoneCatalogOf,
  softZoneOpsSpaceOf,
  softBindCameraCandidates,
  softZoneVolumeVmOf,
  bindCameraToZone,
  unbindCameraFromZone,
  loadZoneCamBinds,
  saveActiveZoneId,
} from "./ops-zone-fabric.js";

const FOCUS_TABS = [
  { id: "run", label: "Run", long: "Run focus" },
  { id: "covers", label: "Cover", long: "Practice covers" },
  { id: "plant", label: "Plant", long: "Plant in ring" },
];

/** Soft≠Live: missing OpenAPI op must not crash the desk. */
function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__ops_zones_status") return "/ops/zones/status";
    if (op === "get__zones") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length ? `/zones?${parts.join("&")}` : "/zones";
    }
    if (op === "get__devices") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length ? `/devices?${parts.join("&")}` : "/devices";
    }
    return "";
  }
}

/** Operator-facing bus errors — never paths or stacks. */
export function softZonesErrorOf(err) {
  const n = String(err == null ? "" : err).trim().toLowerCase();
  if (!n) return "Zone list could not be loaded.";
  if (/\b403\b|forbidden|insufficient|not_authorized|scope/.test(n)) {
    return "Your account cannot open volumes for this site. Ask a site manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(n)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found/.test(n)) {
    return "Volumes status is not available on this console yet.";
  }
  if (/\b429\b|too many/.test(n)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|geofence_read/.test(n)) {
    return "Map volumes service is busy. Try Refresh in a moment.";
  }
  if (/network|failed to fetch|timeout|aborted/.test(n)) {
    return "Network issue — try Refresh.";
  }
  if (/\/ops\/|\/zones|\/devices|resource-fabric|http|get |post |status_path|sql|traceback/.test(n)) {
    return "Zone list could not be loaded.";
  }
  return "Zone list could not be loaded.";
}

export function softZonesSeatLabelOf(role) {
  const r = String(role || "").toLowerCase();
  if (r === "user") return "Operator";
  if (r === "admin") return "Site manager";
  if (r === "superadmin") return "Platform";
  if (r === "sa_staff") return "Staff";
  return role || "Seat";
}

/** Pure: seat from Live volumes posture (Page185). */
export function opsZonesSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const zMax = Number(lim.zone_paint_max);
  const regMax = Number(lim.register_label_max);
  const ringsAllowed = (p.rings_plane_allowed === true || p.list_allowed === true);
  const registerAllowed = (p.register_read_allowed === true);
  const surveyLink = (p.survey_link_allowed === true);
  const fabricWrite = (p.fabric_write_allowed === true);
  const reRead = (p.re_read_allowed === true);
  const softCoverLocal = (p.soft_cover_local_only === true);
  return {
    enabled: (p.enabled === true),
    armed: (p.armed === true),
    ringsAllowed,
    listAllowed: ringsAllowed,
    registerAllowed,
    surveyLinkAllowed: surveyLink,
    fabricWriteAllowed: fabricWrite,
    reReadAllowed: reRead,
    softCoverLocalOnly: softCoverLocal,
    zonePaintMax: Number.isFinite(zMax) && zMax > 0 ? Math.floor(zMax) : 80,
    registerLabelMax: Number.isFinite(regMax) && regMax > 0 ? Math.floor(regMax) : 500,
    role: p.principal_role || null,
    detail: p.detail || null,
  };
}

/** Normalize GET /zones payload → Live zone rows (id = zone_id). */
export function liveZonesOf(pack) {
  const rows = (pack && (pack.items || pack.zones))
    || (Array.isArray(pack) ? pack : []);
  const out = [];
  for (const z of rows) {
    if (!z || typeof z !== "object") continue;
    const id = String(z.zone_id || z.id || "").trim();
    const label = String(z.label || id || "").trim();
    if (!id && !label) continue;
    out.push({
      id: id || label,
      zone_id: id || label,
      label: label || id,
      kind: String(z.kind || ""),
      ring: z.ring || null,
      bbox: z.bbox || null,
      deviceIds: Array.isArray(z.device_ids) ? z.device_ids : (z.deviceIds || []),
      device_ids: Array.isArray(z.device_ids) ? z.device_ids : (z.deviceIds || []),
      count: Number(z.device_count != null ? z.device_count : (z.count || 0)),
      device_count: Number(z.device_count != null ? z.device_count : (z.count || 0)),
      mismatch: z.label_mismatch_device_ids || z.mismatch || [],
      label_mismatch_device_ids: z.label_mismatch_device_ids || z.mismatch || [],
      soft: false,
    });
  }
  return out;
}

function devicesOf(pack) {
  const rows = (pack && (pack.items || pack.devices))
    || (Array.isArray(pack) ? pack : []);
  const mapped = rows
    .filter((d) => d && (d.device_id || d.id || d.serial))
    .map((d) => ({
      id: String(d.device_id || d.id || d.serial || "").trim(),
      label: boardLabelSay(d, d.device_id || d.id || d.serial),
      zone: String(d.zone || d.zone_label || "").trim(),
      offline: !!(d.offline || d.is_offline),
      ll: (d.map_lng != null && d.map_lat != null)
        ? [Number(d.map_lng), Number(d.map_lat)]
        : (d.ll || null),
    }));
  const seen = new Set();
  const out = [];
  for (const d of mapped) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    out.push(d);
  }
  return out;
}

/**
 * @param {{
 *   fleet:any,
 *   toast?:function,
 *   activeZoneId?:string,
 *   onEnterZone?:(id:string, route?:string)=>void,
 *   onClearZone?:()=>void,
 *   liveZones?:any[],
 *   devices?:any[],
 * }} props
 */
export function OpsZonesDesk({
  fleet,
  toast,
  activeZoneId,
  onEnterZone,
  onClearZone,
  liveZones = [],
  devices: devicesProp = [],
}) {
  const local = isLocalHost();
  const [binds, setBinds] = useState(() => loadZoneCamBinds());
  const [acceptPulse, setAcceptPulse] = useState(0);
  const [focusTab, setFocusTab] = useState("run");

  const posture = useApi(safePath("get__ops_zones_status"));
  const post = posture.data || {};
  const seat = opsZonesSeatOf(
    posture.loading || !posture.data
      ? {
          enabled: true,
          rings_plane_allowed: false,
          list_allowed: false,
          register_read_allowed: false,
          survey_link_allowed: false,
          re_read_allowed: false,
          soft_cover_local_only: true,
          limits: { zone_paint_max: 80, register_label_max: 500 },
        }
      : post,
  );

  const zonesR = useApi(seat.ringsAllowed ? safePath("get__zones") : "");
  const devicesR = useApi(
    seat.registerAllowed
      ? safePath("get__devices", { query: { limit: seat.registerLabelMax } })
      : "",
  );

  /* Places survey writes → volumes desk re-reads without waiting for nav. */
  useEffect(() => {
    const onBump = () => {
      zonesR.reload && zonesR.reload();
      devicesR.reload && devicesR.reload();
    };
    window.addEventListener(LIVE_ZONES_CHANGED, onBump);
    return () => window.removeEventListener(LIVE_ZONES_CHANGED, onBump);
  }, [zonesR.reload, devicesR.reload]);

  const softState = local && fleet && fleet.enabled ? fleet.state : null;

  const liveFromBus = useMemo(() => liveZonesOf(zonesR.data), [zonesR.data]);
  const devicesFromBus = useMemo(() => devicesOf(devicesR.data), [devicesR.data]);

  /* Desk bus when settled; parent warm-start while first paint; no Soft invent. */
  const liveList = useMemo(() => {
    if (zonesR.data || zonesR.error) {
      return liveFromBus.slice(0, seat.zonePaintMax);
    }
    const parent = Array.isArray(liveZones) ? liveZones : [];
    return parent.slice(0, seat.zonePaintMax);
  }, [zonesR.data, zonesR.error, liveFromBus, liveZones, seat.zonePaintMax]);

  const devices = useMemo(() => {
    if (devicesR.data || devicesR.error) return devicesFromBus;
    return Array.isArray(devicesProp) ? devicesProp : [];
  }, [devicesR.data, devicesR.error, devicesFromBus, devicesProp]);

  const zones = useMemo(
    () =>
      softZoneCatalogOf({
        zones: liveList,
        devices,
        softState,
        centre: softState && softState.origin,
      }),
    [softState, binds, devices, liveList],
  );

  const zonesHydrated = useMemo(
    () =>
      zones.map((z) => {
        const cams = Array.isArray(binds[z.id]) ? binds[z.id] : z.cameraIds;
        return softZoneVolumeVmOf({ ...z, cameraIds: cams }, softState, devices);
      }),
    [zones, binds, softState, devices],
  );

  const active = zonesHydrated.find((z) => z.id === activeZoneId) || null;
  const space = softZoneOpsSpaceOf(active, { softState, devices, catalogueCameras: [] });
  const candidates = softBindCameraCandidates({ devices, softState, catalogue: [] });
  const boundSet = new Set((active && active.cameraIds) || []);
  const suggest = candidates.filter((c) => !boundSet.has(c.id)).slice(0, 4);

  const liveCount = useMemo(
    () => zonesHydrated.filter((z) => z && !z.soft).length,
    [zonesHydrated],
  );
  const softPadCount = local
    ? zonesHydrated.filter((z) => z && z.soft).length
    : 0;

  const enter = (id) => {
    saveActiveZoneId(id);
    onEnterZone && onEnterZone(id);
    setFocusTab("run");
  };

  const bind = (zoneId, camId) => {
    if (!local || !seat.softCoverLocalOnly) {
      if (!local) {
        toast && toast("Camera covers are for practice workspace only", "warn");
      }
      return;
    }
    const next = bindCameraToZone(zoneId, camId);
    setBinds({ ...next });
    setAcceptPulse((n) => n + 1);
    toast && toast("Cover attached · this browser only", "go");
  };

  const unbind = (zoneId, camId) => {
    if (!local) {
      toast && toast("Camera covers are for practice workspace only", "warn");
      return;
    }
    const next = unbindCameraFromZone(zoneId, camId);
    setBinds({ ...next });
    toast && toast("Cover removed", "warn");
  };

  const acceptSuggested = () => {
    if (!local) {
      toast && toast("Camera covers are for practice workspace only", "warn");
      return;
    }
    if (!active || !suggest.length) return;
    let next = loadZoneCamBinds();
    for (const c of suggest.slice(0, 3)) {
      next = bindCameraToZone(active.id, c.id);
    }
    setBinds({ ...next });
    setAcceptPulse((n) => n + 1);
    toast && toast("Nearby covers attached · this browser only", "go");
  };

  const reload = () => {
    if (!seat.reReadAllowed && !posture.loading) {
      toast && toast("Refresh is closed for this seat", "warn");
      return;
    }
    posture.reload && posture.reload();
    zonesR.reload && zonesR.reload();
    devicesR.reload && devicesR.reload();
    toast && toast("Areas refreshed", "go");
  };

  const openSurvey = () => {
    if (!seat.surveyLinkAllowed) {
      toast && toast("Map survey is closed for this seat", "warn");
      return;
    }
    go("/ops/areas");
  };

  const coverN = active ? active.coverN || 0 : 0;
  const plantN = active ? active.plantN || (active.deviceIds || []).length : 0;
  const coveredAll = zonesHydrated.filter((z) => (z.coverN || 0) > 0).length;
  const mismatchN = active && Array.isArray(active.mismatch)
    ? active.mismatch.length
    : (active && Array.isArray(active.label_mismatch_device_ids)
      ? active.label_mismatch_device_ids.length
      : 0);

  const coversMeter = local
    ? (active ? coverN : coveredAll)
    : 0;
  const pulseLine = local
    ? "Practice workspace — rings from the site map; covers stay in this browser."
    : "Site volumes for your map · plant membership uses map location, not labels.";

  const busErr = zonesR.error
    || (posture.error && !posture.loading
      && !String(posture.error).match(/\b404\b/)
      ? posture.error
      : "");
  const sealed = !seat.ringsAllowed && !posture.loading;
  const seatLabel = softZonesSeatLabelOf(seat.role || post.principal_role);

  const clearFocus = () => {
    saveActiveZoneId("");
    onClearZone && onClearZone();
  };

  return html`<div
    className="ops-hud ops-vols ops-field ops-field--volumes ops-zones ops-vol ops-vol-flat"
    data-cmc-residual="ops-zone-fabric"
    data-soft=${local ? "1" : "0"}
    data-local=${local ? "1" : "0"}
    data-field="v554-zone-plane"
    data-has-space=${active && space.ok ? "1" : "0"}
    data-accept-pulse=${acceptPulse}
    data-live-count=${liveCount}
    data-page="page185"
    data-cmc-ops="ops3-5">

    <div className="ops-hud__scan" aria-hidden="true" />

    <header className="ops-hud__top">
      <div className="ops-hud__brand">
        <span className="ops-hud__chip">Areas</span>
        <span className="ops-hud__chip">${zonesHydrated.length}</span>
        ${local
          ? html`<span className="ops-hud__chip ops-hud__chip--soft">Practice</span>`
          : html`<span className="ops-hud__chip ops-hud__chip--live">On site</span>`}
        <span className="ops-hud__chip ops-hud__chip--seat" title="Seat">${seatLabel}</span>
      </div>
      <div className="ops-hud__top-actions">
        <button type="button" className="chip" data-tone="go"
          title="Focus mode" onClick=${() => go("/ops/areas")}>Focus</button>
        <button type="button" className="chip" data-tone="ai"
          title="Boundary survey" onClick=${openSurvey}>Boundary</button>
        <button type="button" className="ops-hud__refresh"
          title="Refresh areas"
          onClick=${reload}>
          <${Ico} d="refresh" w=${13}/>
        </button>
      </div>
    </header>

    <p className="ops-hud__pulse" title="Areas pulse">${pulseLine}</p>

    ${busErr
      ? html`<${Alert} tone="warn" say=${softZonesErrorOf(busErr)}/>`
      : null}
    ${sealed
      ? html`<${Alert} tone="warn" say="Volumes are closed for this seat."/>`
      : null}
    ${!busErr && !sealed && zonesR.loading && !liveCount
      ? html`<${Alert} tone="info" say="Loading map volumes…"/>`
      : null}

    <div className="ops-hud__meters ops-vols__meters" role="group" aria-label="Zone counts">
      <div className="ops-hud__meter" data-on=${liveCount ? "1" : "0"} data-tone="go">
        <b>${liveCount || "—"}</b><em>Site volumes</em>
        <s className="ops-hud__meter-bar" style=${{
          transform: `scaleX(${liveCount ? Math.min(1, Math.max(0.18, liveCount / 6)) : 0.18})`,
        }}/>
      </div>
      <div className="ops-hud__meter" data-on=${active ? "1" : "0"} data-tone="go">
        <b>${active ? "on" : "—"}</b><em>Focus</em>
        <s className="ops-hud__meter-bar" style=${{ transform: `scaleX(${active ? 1 : 0.18})` }}/>
      </div>
      <div className="ops-hud__meter" data-on=${plantN ? "1" : "0"} data-tone="go">
        <b>${active ? plantN : "—"}</b><em>Plant</em>
        <s className="ops-hud__meter-bar" style=${{
          transform: `scaleX(${plantN ? Math.min(1, Math.max(0.18, plantN / 8)) : 0.18})`,
        }}/>
      </div>
      <div className="ops-hud__meter" data-on=${coversMeter || softPadCount ? "1" : "0"} data-tone="warn">
        <b>${local ? (coversMeter || softPadCount || "0") : "—"}</b>
        <em>${local ? "Covers" : "Covers · lab"}</em>
        <s className="ops-hud__meter-bar" style=${{
          transform: `scaleX(${coversMeter ? Math.min(1, Math.max(0.18, coversMeter / 6)) : 0.18})`,
        }}/>
      </div>
    </div>

    ${!local
      ? html`<div className="ops-vol__banner ops-vols__banner" data-local="0">
          <b>Site volumes</b>
          <p>Rings show geometry for your organisation. Cover binds are practice-only on this desk Soft≠ production camera coverage — commission Live coverage under Places / Fleet. Edit map shapes in Map survey when you have access.</p>
        </div>`
      : html`<div className="ops-vol__banner ops-vols__banner" data-local="1">
          <b>Practice covers</b>
          <p>Bindings stay in this browser only Soft≠ Live fabric. Map standby thumbs never invent a stream. Site volumes still load from the map when seated.</p>
        </div>`}

    <div className="ops-vols__body ops-vol-flat__body">
      <section className="ops-vols__roster" aria-label="Zones">
        <div className="ops-hud__lane-h">
          <p className="ops-hud__k"><s></s>Rings</p>
          <em className="ops-vols__match">${zonesHydrated.length || 0}</em>
        </div>
        ${!seat.ringsAllowed
          ? html`<div className="ops-hud__quiet ops-vol-flat__empty">
              <b>Volumes closed</b>
              · This seat cannot list map rings.
            </div>`
          : !zonesHydrated.length
          ? html`<div className="ops-hud__quiet ops-vol-flat__empty">
              <b>${zonesR.loading ? "Checking volumes…" : "No map rings yet"}</b>
              · Ask a site manager to draw volumes on the map survey when ready.
              ${seat.surveyLinkAllowed
                ? html`<button type="button" className="btn btn--sm btn--go" style=${{ marginTop: 8 }}
                    onClick=${openSurvey}>
                    Open map survey
                  </button>`
                : null}
            </div>`
          : html`<div className="ops-vol-flat__list ops-vols__list" role="list">
              ${zonesHydrated.map(
                (z, i) => html`
                  <${M.button}
                    key=${z.id}
                    type="button"
                    role="listitem"
                    className="ops-vol-flat__row ops-vols__row"
                    data-on=${activeZoneId === z.id ? "1" : "0"}
                    data-ready=${z.ready || "open"}
                    data-soft=${z.soft ? "1" : "0"}
                    title=${"Focus " + (z.label || z.id)}
                    initial=${{ opacity: 0, y: 8 }}
                    animate=${{ opacity: 1, y: 0 }}
                    whileHover=${{ y: -1 }}
                    whileTap=${{ scale: 0.98 }}
                    transition=${{ ...SOFT, delay: Math.min(0.12, 0.025 * i) }}
                    onClick=${() => enter(z.id)}>
                    <span className="ops-vol-flat__row-main ops-vols__main">
                      <b>${z.label}</b>
                      <u>${z.soft ? "Practice pad" : z.roleLabel || "Site ring"}</u>
                    </span>
                    <em className="ops-vols__trail" data-ready=${z.ready || "open"} data-soft=${z.soft ? "1" : "0"}>
                      ${(z.deviceIds && z.deviceIds.length) || z.plantN || 0}p
                      ${local ? ` · ${z.coverN || 0}c` : ""}
                    </em>
                    ${activeZoneId === z.id
                      ? html`<${M.i}
                          layoutId="ops-vol-flat-mark"
                          className="ops-vol-flat__mark ops-vols__mark"
                          aria-hidden="true"
                          transition=${SPRING}/>`
                      : null}
                  <//>`,
              )}
            </div>`}
      </section>

      ${active && space.ok
        ? html`<${M.section}
            key=${"space-" + active.id}
            className="ops-vols__focus ops-vol-flat__focus"
            data-pulse=${acceptPulse}
            aria-label="Active zone"
            initial=${{ opacity: 0, y: 10 }}
            animate=${{ opacity: 1, y: 0 }}
            transition=${SPRING}>
            <div className="ops-vols__focus-hd">
              <div className="ops-vols__focus-brand">
                <span className="ops-hud__chip">${active.soft ? "Practice pad" : "Site volume"}</span>
                <b className="ops-vols__focus-name">${active.label}</b>
                <p className="ops-vols__focus-sub">
                  ${plantN
                    ? `${plantN} plant in ring · Run scope`
                    : active.soft
                      ? "Practice pad · not a site survey ring"
                      : "Empty ring — place devices on the map inside this area"}
                  ${mismatchN ? ` · ${mismatchN} label vs location mismatch` : ""}
                </p>
              </div>
              <button type="button" className="btn btn--ghost btn--xs" onClick=${clearFocus}>
                Clear
              </button>
            </div>

            <div className="ops-seg ops-vols__tabs" role="tablist" aria-label="Volume focus">
              ${FOCUS_TABS.map((t) => html`
                <button
                  key=${t.id}
                  type="button"
                  role="tab"
                  className="ops-seg__btn"
                  data-on=${focusTab === t.id ? "1" : "0"}
                  title=${t.id === "covers" && !local ? "Camera covers · practice only" : (t.long || t.label)}
                  onClick=${() => setFocusTab(t.id)}>
                  ${t.label}
                </button>`)}
            </div>

            ${focusTab === "run" ? html`
              <div className="ops-vols__stage">
                <div className="ops-vols__cmd ops-vol-flat__cmd">
                  <button type="button" className="btn btn--go ops-hud__cta"
                    onClick=${() => onEnterZone && onEnterZone(active.id, "/ops/workbench")}>
                    Continue in Run
                  </button>
                  <div className="ops-vols__cmd-row">
                    <button type="button" className="btn btn--ghost btn--sm" onClick=${() => enter(active.id)}>
                      Frame map
                    </button>
                    <button type="button" className="btn btn--ghost btn--sm" onClick=${openSurvey}>
                      Map survey
                    </button>
                  </div>
                </div>
                ${local && !coverN && suggest.length
                  ? html`<${M.div}
                      className="ops-vols__suggest ops-vol-flat__suggest"
                      initial=${{ opacity: 0, y: 6 }}
                      animate=${{ opacity: 1, y: 0 }}
                      transition=${SOFT}>
                      <span>
                        <b>Attach ${Math.min(3, suggest.length)} nearby</b>
                        <i>Practice covers · this browser only</i>
                      </span>
                      <button type="button" className="btn btn--sm btn--go" onClick=${acceptSuggested}>
                        Accept
                      </button>
                    <//>`
                  : null}
              </div>
            ` : null}

            ${focusTab === "covers" ? html`
              <div className="ops-vols__stage">
                ${!local
                  ? html`<div className="ops-hud__quiet">
                      <b>Camera covers · practice only</b>
                      Live coverage is commissioned under Places / Fleet. Plant membership still follows map location.
                    </div>`
                  : html`
                    <div className="ops-hud__lane-h">
                      <p className="ops-hud__k"><s></s>Covers · practice</p>
                      <em className="ops-vols__match">${space.cameras.length || 0}</em>
                    </div>
                    <div className="ops-vols__covers ops-vol-flat__covers" role="list" data-pulse=${acceptPulse}>
                      ${space.cameras.length
                        ? space.cameras.map(
                            (c, i) => html`
                              <${M.div}
                                key=${c.id}
                                className="ops-vols__cover ops-vol-flat__cover"
                                role="listitem"
                                initial=${{ opacity: 0, x: -4 }}
                                animate=${{ opacity: 1, x: 0 }}
                                transition=${{ ...SOFT, delay: Math.min(0.1, 0.03 * i) }}>
                                <b title=${c.label}>${c.label}</b>
                                <button type="button" className="btn btn--ghost btn--xs"
                                  onClick=${() => unbind(active.id, c.id)}>Remove</button>
                              <//>`,
                          )
                        : html`<p className="ops-hud__quiet ops-vol-flat__empty">No covers yet · Accept nearby on Run tab</p>`}
                    </div>
                    <div className="ops-hud__lane-h" style=${{ marginTop: 8 }}>
                      <p className="ops-hud__k"><s></s>Plant cameras · attach</p>
                    </div>
                    <div className="ops-vols__chips ops-vol-flat__chips" role="list">
                      ${candidates.length
                        ? candidates.map(
                            (c, i) => html`
                              <${M.button}
                                key=${c.id}
                                type="button"
                                role="listitem"
                                className="ops-vols__chip ops-vol-flat__chip"
                                data-on=${boundSet.has(c.id) ? "1" : "0"}
                                data-suggest=${!boundSet.has(c.id) && suggest.some((s) => s.id === c.id) ? "1" : "0"}
                                disabled=${boundSet.has(c.id)}
                                initial=${{ opacity: 0, scale: 0.96 }}
                                animate=${{ opacity: 1, scale: 1 }}
                                whileTap=${boundSet.has(c.id) ? undefined : { scale: 0.96 }}
                                transition=${{ ...SOFT, delay: Math.min(0.12, 0.025 * i) }}
                                onClick=${() => bind(active.id, c.id)}>
                                <b>${c.label}</b>
                                <i>${boundSet.has(c.id) ? "ON" : "ADD"}</i>
                              <//>`,
                          )
                        : html`<p className="ops-hud__quiet ops-vol-flat__empty">No plant cameras yet.</p>`}
                    </div>`}
              </div>
            ` : null}

            ${focusTab === "plant" ? html`
              <div className="ops-vols__stage">
                <div className="ops-hud__lane-h">
                  <p className="ops-hud__k"><s></s>Plant in ring</p>
                  <em className="ops-vols__match">${plantN}</em>
                </div>
                <div className="ops-vols__chips ops-vol-flat__chips" role="list">
                  ${(active.deviceIds || []).length
                    ? (active.deviceIds || []).slice(0, 16).map(
                        (id) => html`
                          <span key=${id} className="ops-vols__chip ops-vol-flat__chip" data-on="1" role="listitem">
                            <b title=${id}>${String(id).slice(0, 14)}</b>
                            <i>LIVE</i>
                          </span>`,
                      )
                    : html`<p className="ops-hud__quiet ops-vol-flat__empty">
                        No devices with map location inside this ring for your seat.
                      </p>`}
                </div>
                <p className="ops-vols__note">Membership uses map location. Practice covers stay off site players.</p>
              </div>
            ` : null}
          <//>`
        : html`<section className="ops-vols__idle ops-vol-flat__idle" aria-label="Pick zone">
            <b>Choose a volume</b>
            <p>Focus a ring — plant list and Run scope follow that volume.</p>
            ${zonesHydrated[0]
              ? html`<button type="button" className="btn btn--sm btn--go"
                  onClick=${() => enter(zonesHydrated[0].id)}>
                  Open ${zonesHydrated[0].label}
                </button>`
              : seat.surveyLinkAllowed
                ? html`<button type="button" className="btn btn--sm" onClick=${openSurvey}>
                    Open map survey
                  </button>`
                : null}
          </section>`}
    </div>

    <footer className="ops-hud__foot ops-vols__foot ops-vol-flat__foot">
      <span className="ops-hud__foot-role" data-soft=${local ? "1" : "0"}>
        ${local ? "practice covers · site rings" : "Site rings · covers in practice only"}
      </span>
    </footer>
  </div>`;
}

export default OpsZonesDesk;
